import HttpRequest from '@/../bower_components/teknisavuecomponents/assets/utils/HttpRequest';
import store from '@/store/store';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import ExpectedError from '@/../bower_components/teknisavuecomponents/assets/utils/ExpectedError';
import MarcacaoRepository from "../offline/repository/MarcacaoRepository";
import { MarcacaoOfflineResponse } from '../interfaces/Offline';
import OfflineController from '@/assets/ts/controller/OfflineController';
import ParallelRequestHandler from '@/assets/ts/utils/ParallelRequestHandler';

interface LimparMarcacoesResponse {
  MARCACOES_SINCRONIZADAS: MarcacaoOfflineResponse[],
  MARCACOES_COM_ERRO: MarcacaoOfflineResponse[]
}

/**
 * A classe SincronizacaoController gerencia a sincronização de dados offline com o servidor.
 * Esta classe é responsável por iniciar a sincronização e limpeza das marcações offline.
 * 
 * Métodos:
 * 
 * - `iniciarFluxoOnline(isManual: boolean)`: Inicia o processo de sincronização quando o dispositivo volta a ficar online.
 * - `iniciarSincronizacaoMarcacoes(showLoading: boolean)`: Sincroniza as marcações offline com o servidor.
 * - `iniciarLimpezaMarcacoes(showLoading: boolean)`: Limpa as marcações offline após a sincronização com o servidor.
 * - `limparMarcacoesOffline(marcacoes: LimparMarcacoesResponse)`: Realiza a limpeza local das marcações offline, removendo as sincronizadas e atualizando as que tiveram erros.
 */
export default class SincronizacaoController {
  private static messages = {
    sincronizacaoIniciada: 'A sincronização em segundo plano foi iniciada!',
    sincronizacaoFinalizada: 'A sincronização em segundo plano foi concluída com sucesso!',
    sincronizacaoEmAndamento: 'A sincronização está em andamento. Não é possível iniciar uma nova sincronização neste momento!',
    semMarcacoes: 'Não há marcações para sincronizar!',
    onlineRequerido: 'É necessário estar conectado à internet para iniciar a sincronização!',
    erroSincronizacao: 'Não foi possível enviar as marcações offline para sincronização!',
    erroEnviarLimpeza: 'Não foi possível limpar as marcações offline!',
    erroRealizarLimpeza: 'SQLite - Erro ao fazer a limpeza do Cache Offline'
  };

  // Propriedade estática para controlar o estado da sincronização
  private static sincronizacaoEmAndamento = false;

  private static get reloadCache(): number {
    return store.getters.offline.reloadCache;
  }

  /**
   * Inicia o processo de sincronização quando o dispositivo volta a ficar online.
   * @param isManual Indica se a sincronização foi iniciada manualmente.
  */
  public static async iniciarFluxoOnline(isManual =  false, retryAttempt = 0) {
    const maxRetries = 1;

    const marcacoes = await MarcacaoRepository.getMarcacoes();

    if(!marcacoes || !marcacoes.length) {
      if(isManual) {
        SnackbarUtils.showMessage(this.messages.semMarcacoes, SnackbarUtils.TYPE_ERROR);
      }
      return;
    }

    if(this.sincronizacaoEmAndamento) {
      if(isManual) {
        SnackbarUtils.showMessage(this.messages.sincronizacaoEmAndamento, SnackbarUtils.TYPE_ERROR);
      }
      return;
    }

    //delay de 3s para caso a internet demore se conectar
    await this.sleep(3000);

    // Marca a sincronização como iniciada
    this.sincronizacaoEmAndamento = true;

    try {
      if(isManual) {
        SnackbarUtils.showMessage(this.messages.sincronizacaoIniciada, SnackbarUtils.TYPE_INFO); 
      }

      await this.iniciarSincronizacaoMarcacoes(isManual, false);
      await this.iniciarLimpezaMarcacoes(false);

      if(isManual) {
        SnackbarUtils.showMessage(this.messages.sincronizacaoFinalizada, SnackbarUtils.TYPE_SUCCESS); 
      }
      
    } catch (error) {

      // Verifica se pode tentar mais uma vez
      if (retryAttempt < maxRetries) {
        await this.sleep(5000);
        await this.iniciarFluxoOnline(isManual, retryAttempt + 1);
      }

    } finally {
      this.sincronizacaoEmAndamento = false;
    }
  }

  /**
   * Sincroniza as marcações offline com o servidor.
   * @param showLoading Indica se deve mostrar um carregamento durante a sincronização.
   * @returns Verdadeiro se a sincronização foi bem-sucedida, falso caso contrário.
  */
  public static async iniciarSincronizacaoMarcacoes(isManual = false, showLoading = true) {
    const marcacoes = await MarcacaoRepository.getMarcacoes();

    if(!marcacoes || !marcacoes.length) return;

    const nrOrg: string = store.getters.user.nrorg;
    const route = 'sincronizarMarcacoesIntervaloOffline';

    // Função para gerar os parâmetros para cada chunk de marcações
    const generateParams = (chunk: any[]) => ({
      requestType: 'Row',
      NRORG: nrOrg,
      row: {
        MARCACOES: chunk,
      },
    });

    // Criação da instância do ParallelRequestHandler
    const handler = new ParallelRequestHandler('post', route, showLoading);

    try {
      const success = await handler.executeInParallel(
        marcacoes,
        generateParams // Função para gerar os parâmetros
      );

      return success;

    } catch (err) {
      const defaultError = this.messages.erroSincronizacao;
      const message = err && (err as any).response ? (err as any).response.data.error : defaultError;
      console.log(message);
      //SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      throw err;
    }
  }

  /**
   * Limpa as marcações offline após a sincronização com o servidor.
   * @param showLoading Indica se deve mostrar um carregamento durante a limpeza.
  * @returns Verdadeiro se a limpeza foi bem-sucedida, falso caso contrário.
  */
  public static async iniciarLimpezaMarcacoes(showLoading = true) {
    const marcacoes = await MarcacaoRepository.getMarcacoes();

    if(!marcacoes || !marcacoes.length) return;

    const nrOrg: string = store.getters.user.nrorg;

    const route = 'limparCacheMarcacoesOffline';

    const params = {
      requestType: 'Row',
      NRORG: nrOrg,
      row: {
        MARCACOES: marcacoes,
      },
    };

    try {
      const response = await HttpRequest.post(route, params, showLoading);
      ExpectedError.throwResponseError(response);

      const marcacoes: LimparMarcacoesResponse = response.data.dataset.data;

      await this.limparMarcacoesOffline(marcacoes);

      store.commit('updateOffline', {
        ...store.getters.offline,
        reloadCache: this.reloadCache + 1
      });

      return true;

    } catch (err) {
      const defaultError = this.messages.erroEnviarLimpeza;
      const message = err && (err as any).response ? (err as any).response.data.error : defaultError;
      console.log(message);
      //SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      throw err;
    }
  }

  /**
   * Realiza a limpeza local das marcações offline, removendo as sincronizadas e atualizando as que tiveram erros.
   * @param marcacoes A resposta da sincronização, contendo as marcações a serem limpas.
  */
  private static async limparMarcacoesOffline(marcacoes: LimparMarcacoesResponse) {
    const marcacoesSincronizadas = marcacoes.MARCACOES_SINCRONIZADAS;
    const marcacoesComErro = marcacoes.MARCACOES_COM_ERRO;
    
    try {

      await Promise.all([
        MarcacaoRepository.deleteMarcacoes(marcacoesSincronizadas),
        MarcacaoRepository.updateMarcacoes(marcacoesComErro)
      ]);

    } catch (error) {
      console.log(this.messages.erroRealizarLimpeza);
    }
  }

  private static sleep(ms) {
    return new Promise((resolve: any) => setTimeout(resolve, ms));
  }
}